import cameraOffSvg from '@raison/static/icons/camera-off-line.svg';
import closeIconSvg from '@raison/static/icons/close-line.svg';
import qrScanIcon from '@raison/static/icons/qr-scan-2-line.svg';
import {
    cva,
    InlineSvg,
    PortalTo,
    RsnButton,
    RsnDialog,
    RsnSpinner,
    useNonReactiveCallback,
    useRootElement,
} from '@raison/ui-kit';
import QrScanner from 'qr-scanner';
import React, {ElementRef, Fragment, useReducer, useRef} from 'react';
import {GenericErrorMsg} from '~/entities/dialogs';
import {Permission, usePermission} from '~/shared/permissions-browser/usePermission';
import {ShellEventBus} from '~/shared/shell-bridge/ShellEventBus';

const ACCESS_ERROR = 'Camera not found.';

function QrScannerOverlay(props: {
    permission: NonNullable<Permission>;
    requestPermission: () => Promise<boolean>;
    state: ScannerState;
    onResult: (result: string) => void;
    onStart: () => void;
    onCancel: (readerIsOpen: boolean) => void;
    onError: (error: unknown) => void;
}) {
    const scanner = useRef<QrScanner>();

    const onDecode = useNonReactiveCallback(result => {
        scanner.current?.stop();
        props.onResult(result.data);
    });

    const scannerRefCb = useNonReactiveCallback(async (video: ElementRef<'video'> | null) => {
        if (video === null) {
            scanner.current?.destroy();
            scanner.current = undefined;
            return;
        }
        let isPermitted = Permission.isGranted(props.permission);
        if (!isPermitted) {
            isPermitted = await props.requestPermission();
        }
        if (!isPermitted) {
            props.onError(ACCESS_ERROR);
            return;
        }
        scanner.current = new QrScanner(video, onDecode, {
            highlightScanRegion: true,
            highlightCodeOutline: true,
            maxScansPerSecond: 1,
            preferredCamera: 'environment',
        });
        scanner.current.start().then(props.onStart).catch(props.onError);
    });

    const scannerIsVisible = Scanner.isScanning(props.state);

    return (
        <PortalTo element={useRootElement()}>
            <div className={cls.overlay({visible: scannerIsVisible})} onClick={() => props.onCancel(false)}>
                <div>
                    <video ref={scannerRefCb} style={{width: '100vmin', aspectRatio: 1}} />
                </div>
                <InlineSvg className={cls.closeBtn()} src={closeIconSvg} />
            </div>
        </PortalTo>
    );
}

type ScannerState = 'idle' | 'pending' | 'scanning';
type ScannerModel = {
    state: ScannerState;
    dialog: null | React.JSX.Element;
};
namespace Scanner {
    export const isNotIdle = (s: ScannerState) => s !== 'idle';
    export const isIdle = (s: ScannerState) => s === 'idle';
    export const isPending = (s: ScannerState) => s === 'pending';
    export const isScanning = (s: ScannerState) => s === 'scanning';
}
type ScannerAction =
    | {type: 'toggle'; permission: Permission}
    | {type: 'success'}
    | {type: 'start'}
    | {type: 'cancel'}
    | {type: 'error'; error: unknown};
function reduceScannerState(model: ScannerModel, action: ScannerAction): ScannerModel {
    switch (action.type) {
        case 'toggle': {
            const {permission} = action;
            if (Scanner.isPending(model.state)) {
                return {state: 'idle', dialog: null};
            }
            if (Permission.isDenied(permission)) {
                return {state: 'idle', dialog: <CameraAccessDialog />};
            }
            return {dialog: null, state: 'pending'};
        }
        case 'start':
            return {dialog: null, state: 'scanning'};
        case 'success':
        case 'cancel':
            return {dialog: null, state: 'idle'};
        case 'error': {
            let dialog = model.dialog;
            if (action.error === ACCESS_ERROR) {
                dialog = <CameraAccessDialog />;
            } else {
                dialog = <GenericErrorMsg.Dialog />;
            }
            return {dialog, state: 'idle'};
        }
        default:
            return model;
    }
}

export function QrScannerButton({onScan}: {onScan: (result: string) => void}) {
    const [model, dispatch] = useReducer(reduceScannerState, {state: 'idle', dialog: null});
    const [permission, requestPermission] = usePermission('camera');

    return (
        <Fragment>
            <button
                type='button'
                onClick={() => dispatch({type: 'toggle', permission})}
                tabIndex={-1}
                className='w-full'
            >
                {Scanner.isPending(model.state) ? <RsnSpinner /> : <InlineSvg src={qrScanIcon} />}
            </button>
            {Scanner.isNotIdle(model.state) && Permission.isReady(permission) && (
                <QrScannerOverlay
                    permission={permission}
                    requestPermission={requestPermission}
                    state={model.state}
                    onResult={result => {
                        dispatch({type: 'success'});
                        onScan(result);
                    }}
                    onStart={() => dispatch({type: 'start'})}
                    onCancel={() => dispatch({type: 'cancel'})}
                    onError={error => dispatch({type: 'error', error})}
                />
            )}
            <RsnDialog.Controller onClose={() => dispatch({type: 'cancel'})} children={model.dialog} />
        </Fragment>
    );
}

function CameraAccessDialog() {
    const sendSettingsIntent = ShellEventBus.useSendEvent('PermissionSettingsIntent');
    return (
        <RsnDialog.Window>
            <RsnDialog.CenteredHeader icon={RsnDialog.framedIcon(cameraOffSvg)}>
                {'Camera access required'}
            </RsnDialog.CenteredHeader>
            <RsnDialog.Body>{'Please enable camera access in your device settings to continue.'}</RsnDialog.Body>
            <RsnDialog.Footer>
                {sendSettingsIntent ? (
                    <RsnButton.Primary onClick={() => sendSettingsIntent()}>{'Go to Settings'}</RsnButton.Primary>
                ) : (
                    <RsnButton.Alike intent={'secondary'} as={RsnDialog.CloseTrigger}>
                        {'I got it'}
                    </RsnButton.Alike>
                )}
            </RsnDialog.Footer>
        </RsnDialog.Window>
    );
}

const cls = {
    overlay: cva(
        'fixed inset-0 z-50 flex flex-col items-center justify-center bg-shade-70 p-4 transition-opacity duration-200',
        {
            variants: {
                visible: {true: 'opacity-1', false: 'opacity-0'},
            },
            defaultVariants: {
                visible: false,
            },
        }
    ),
    closeBtn: cva('size-6 self-center text-neutral-10'),
};
